import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useToast,
  Alert,
  AlertIcon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Select,
  HStack,
  VStack,
  Icon,
  Spinner,
  Center,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  UnorderedList,
  ListItem,
  Grid,
} from '@chakra-ui/react';
import { FaSearch, FaTimes, FaSort, FaSortUp, FaSortDown, FaQuestion, FaEye } from 'react-icons/fa';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';
import { useMenuTitle } from '../hooks/useMenuTitle';
import { useDisclosure } from '@chakra-ui/react';

const MessageTemplateAuditLog = ({ isModal = false }) => {
  const toast = useToast();
  const menuTitle = useMenuTitle();
  const [auditLogs, setAuditLogs] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userEmails, setUserEmails] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [sortField, setSortField] = useState('performed_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedChange, setSelectedChange] = useState(null);
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isHelpOpen, onOpen: onHelpOpen, onClose: onHelpClose } = useDisclosure();
  const PAGE_SIZE = 50;

  // Check admin status once on mount
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_administrator')
          .eq('id', user.id)
          .single();
          
        setIsAdmin(profile?.is_administrator || false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  // Fetch user names
  const fetchUserNames = async (userIds) => {
    try {
      if (!userIds || userIds.length === 0) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (error) {
        console.error('Error fetching user names:', error);
        return;
      }

      const nameMap = {};
      data.forEach(user => {
        nameMap[user.id] = user.full_name || 'Unknown User';
      });

      setUserEmails(nameMap);
    } catch (error) {
      console.error('Error fetching user names:', error);
    }
  };

  // Load audit logs when filters or sort changes
  useEffect(() => {
    if (isAdmin) {
      loadAuditLogs(true);
    }
  }, [isAdmin, timeFilter, actionFilter, sortField, sortDirection]);

  // Load more data when scrolling
  useEffect(() => {
    if (page > 0) {
      loadAuditLogs(false);
    }
  }, [page]);

  const loadAuditLogs = async (resetData = false) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('message_template_audit_logs')
        .select(`
          id,
          template_name,
          action_type,
          performed_at,
          performed_by,
          previous_content,
          new_content
        `)
        .order(sortField, { ascending: sortDirection === 'asc' });

      // Apply time filter
      if (timeFilter !== 'all') {
        const now = new Date();
        let startDate = new Date();
        
        switch (timeFilter) {
          case 'today':
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setDate(now.getDate() - 30);
            break;
        }
        
        query = query.gte('performed_at', startDate.toISOString());
      }

      // Apply action filter
      if (actionFilter !== 'all') {
        query = query.eq('action_type', actionFilter);
      }

      if (resetData) {
        setPage(0);
        setHasMore(true);
      } else {
        query = query
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get unique user IDs for name lookup
      const userIds = [...new Set(data.map(log => log.performed_by))];
      await fetchUserNames(userIds);

      if (resetData) {
        setAuditLogs(data || []);
      } else {
        setAuditLogs(prev => [...prev, ...(data || [])]);
      }

      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast({
        title: 'Error loading audit logs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Intersection observer for infinite scroll
  const observerTarget = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage(prev => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading]);

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort color="gray.300" />;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter logs based on search query
  const filteredLogs = useMemo(() => {
    if (!searchQuery) return auditLogs;

    const query = searchQuery.toLowerCase();
    return auditLogs.filter(log => 
      log.template_name?.toLowerCase().includes(query) ||
      userEmails[log.performed_by]?.toLowerCase().includes(query) ||
      log.action_type?.toLowerCase().includes(query)
    );
  }, [auditLogs, searchQuery, userEmails]);

  const handleViewChanges = (log) => {
    setSelectedChange(log);
    onViewOpen();
  };

  // Help content
  const helpContent = (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text>
          The Message Template Audit Log tracks all changes made to message templates in the system. 
          Each entry shows who made the change, when it was made, and what was changed.
        </Text>
      </Box>

      <Box>
        <Text fontWeight="bold" mb={2}>Features:</Text>
        <UnorderedList spacing={1} pl={4}>
          <ListItem>Search by template name or user</ListItem>
          <ListItem>Filter by action type (Created, Updated, Deleted)</ListItem>
          <ListItem>Filter by time period (Today, Last 7 Days, Last 30 Days)</ListItem>
          <ListItem>Sort any column by clicking the header</ListItem>
          <ListItem>Infinite scroll for viewing large audit histories</ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Text fontWeight="bold" mb={2}>Action Types:</Text>
        <UnorderedList spacing={1} pl={4}>
          <ListItem>
            <Badge fontSize="2xs" colorScheme="green">CREATE</Badge>
            {' - '}New template was created
          </ListItem>
          <ListItem>
            <Badge fontSize="2xs" colorScheme="blue">UPDATE</Badge>
            {' - '}Template content or category was modified
          </ListItem>
          <ListItem>
            <Badge fontSize="2xs" colorScheme="red">DELETE</Badge>
            {' - '}Template was deleted
          </ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Text fontWeight="bold" mb={2}>Changes Column:</Text>
        <UnorderedList spacing={1} pl={4}>
          <ListItem>"Created new template" - A new template was added</ListItem>
          <ListItem>"Content updated" - The template's message content was changed</ListItem>
          <ListItem>"Template category updated" - The template's category was changed</ListItem>
          <ListItem>"Template deleted" - The template was removed from the system</ListItem>
        </UnorderedList>
      </Box>
    </VStack>
  );

  if (!isAdmin) {
    return null;
  }

  return (
    <Box p={isModal ? 2 : 4} h={isModal ? "calc(90vh - 80px)" : "calc(100vh - 100px)"} display="flex" flexDirection="column">
      {!isModal && (
        <HStack justify="space-between" mb={2}>
          <Heading size="lg">{menuTitle}</Heading>
          <IconButton
            icon={<FaQuestion />}
            aria-label="Help"
            size="sm"
            variant="ghost"
            onClick={onHelpOpen}
          />
        </HStack>
      )}

      <VStack spacing={1} align="stretch">
        <Grid templateColumns="1fr 140px 160px" gap={2}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaSearch} color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search by template name or user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              pr="4.5rem"
              size="sm"
            />
            {searchQuery && (
              <InputRightElement>
                <IconButton
                  icon={<FaTimes />}
                  size="xs"
                  variant="ghost"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                />
              </InputRightElement>
            )}
          </InputGroup>

          <Select
            size="sm"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
          >
            <option value="all">All Actions</option>
            <option value="CREATE">Created</option>
            <option value="UPDATE">Updated</option>
            <option value="DELETE">Deleted</option>
          </Select>

          <Select
            size="sm"
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </Select>
        </Grid>

        <Box flex="1" overflowY="auto" mt={0}>
          <Table variant="simple" size="xs">
            <Thead position="sticky" top={0} bg="white" zIndex={1}>
              <Tr>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('performed_at')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>DATE</Text>
                    {getSortIcon('performed_at')}
                  </HStack>
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('template_name')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>TEMPLATE</Text>
                    {getSortIcon('template_name')}
                  </HStack>
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('action_type')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>ACTION</Text>
                    {getSortIcon('action_type')}
                  </HStack>
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('performed_by')}>
                  <HStack spacing={1} display="inline-flex" alignItems="center">
                    <Text>USER</Text>
                    {getSortIcon('performed_by')}
                  </HStack>
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal">ACTIONS</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading && !auditLogs.length ? (
                <Tr>
                  <Td colSpan={5} textAlign="center">
                    <Spinner size="sm" />
                  </Td>
                </Tr>
              ) : auditLogs.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center">
                    <Text fontSize="sm">No audit logs found</Text>
                  </Td>
                </Tr>
              ) : (
                filteredLogs.map((log) => (
                  <Tr key={log.id}>
                    <Td py={0} px={1} fontSize="xs" whiteSpace="nowrap">
                      {format(new Date(log.performed_at), 'MMM d, yyyy h:mm a')}
                    </Td>
                    <Td py={0} px={1} fontSize="xs">{log.template_name}</Td>
                    <Td py={0} px={1} fontSize="xs">
                      <Badge
                        fontSize="2xs"
                        colorScheme={
                          log.action_type === 'CREATE' ? 'green' :
                          log.action_type === 'UPDATE' ? 'blue' :
                          log.action_type === 'DELETE' ? 'red' : 'gray'
                        }
                      >
                        {log.action_type}
                      </Badge>
                    </Td>
                    <Td py={0} px={1} fontSize="xs">
                      {userEmails[log.performed_by] || 'Unknown User'}
                    </Td>
                    <Td py={0} px={1} fontSize="xs">
                      <IconButton
                        icon={<FaEye />}
                        size="xs"
                        variant="ghost"
                        onClick={() => handleViewChanges(log)}
                        aria-label="View template changes"
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
          {loading && auditLogs.length > 0 && (
            <Center p={2}>
              <Spinner size="sm" />
            </Center>
          )}
          <Box ref={observerTarget} h="20px" />
        </Box>
      </VStack>
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>Template Changes</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            pb={6}
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                width: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--chakra-colors-gray-300)',
                borderRadius: '24px',
              },
            }}
          >
            {selectedChange && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>Template Name:</Text>
                  <Text>{selectedChange.template_name}</Text>
                </Box>
                
                <HStack spacing={6}>
                  <Box>
                    <Badge
                      fontSize="sm"
                      colorScheme={
                        selectedChange.action_type === 'CREATE' ? 'green' :
                        selectedChange.action_type === 'UPDATE' ? 'blue' :
                        selectedChange.action_type === 'DELETE' ? 'red' : 'gray'
                      }
                    >
                      {selectedChange.action_type}
                    </Badge>
                  </Box>

                  <Text>
                    <Text as="span" fontWeight="bold">By:</Text>{' '}
                    {userEmails[selectedChange.performed_by] || 'Unknown User'}
                  </Text>

                  <Text>
                    <Text as="span" fontWeight="bold">Date:</Text>{' '}
                    {format(new Date(selectedChange.performed_at), 'yyyy-MM-dd HH:mm:ss')}
                  </Text>
                </HStack>

                {selectedChange.action_type === 'UPDATE' && (
                  <>
                    <Box>
                      <Text fontWeight="bold" mb={2}>Previous Content:</Text>
                      <Box p={2} bg="gray.50" borderRadius="md">
                        <Text whiteSpace="pre-wrap">{selectedChange.previous_content}</Text>
                      </Box>
                    </Box>

                    <Box>
                      <Text fontWeight="bold" mb={2}>New Content:</Text>
                      <Box p={2} bg="gray.50" borderRadius="md">
                        <Text whiteSpace="pre-wrap">{selectedChange.new_content}</Text>
                      </Box>
                    </Box>
                  </>
                )}

                {selectedChange.action_type === 'CREATE' && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Initial Content:</Text>
                    <Box p={2} bg="gray.50" borderRadius="md">
                      <Text whiteSpace="pre-wrap">{selectedChange.new_content}</Text>
                    </Box>
                  </Box>
                )}

                {selectedChange.action_type === 'DELETE' && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>Deleted Content:</Text>
                    <Box p={2} bg="gray.50" borderRadius="md">
                      <Text whiteSpace="pre-wrap">{selectedChange.previous_content}</Text>
                    </Box>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={isHelpOpen} onClose={onHelpClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Message Template Audit Log Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {helpContent}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MessageTemplateAuditLog;
